import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../i18n';
import { trackEvent } from '../analytics';
import '../PortfolioV2.css';

const GOALS = {
  creation: 'present',
  booking: 'booking',
  takeover: 'finish',
};

const COPY = {
  fr: {
    back: 'Retour au portfolio',
    estimate: 'Estimer ce projet',
    start: 'Le point de départ',
    scope: 'Ce que je peux prendre en charge',
    budget: 'Ordre de grandeur',
    budgetNote: 'Le devis final dépend du périmètre utile et de l’existant. Le but est de lancer une première version cohérente, pas d’empiler des fonctions.',
    ctaTitle: 'Votre besoin est encore flou ?',
    ctaText: 'Le simulateur vous aide à choisir les fonctions utiles et à situer le budget avant même de me contacter.',
    cta: 'Faire une estimation',
    contact: 'Me parler du projet',
    example: 'Découvrir une réalisation client',
    services: {
      creation: {
        eyebrow: 'Créer',
        title: 'Création de site internet professionnel pour votre activité.',
        intro: 'Un bon site ne commence pas par React, WordPress ou une liste de technologies. Il commence par ce que vos clients doivent comprendre, faire ou acheter.',
        problem: 'Vous partez de zéro, votre présence actuelle est trop limitée ou votre activité a besoin d’un site plus clair, plus crédible et plus simple à utiliser.',
        work: ['Site vitrine ou landing page', 'Formulaires et demandes de devis', 'Présentation de prestations ou catalogue', 'Version mobile et mise en ligne', 'E-commerce si le projet le nécessite'],
        budget: '850 € à 1 250 € pour un site vitrine simple. Les projets avec vente, comptes ou logique métier sont chiffrés selon les fonctions réellement utiles.',
        meta: 'Création de site web sur mesure pour indépendants et petites entreprises : site vitrine, landing page, e-commerce et formulaires.',
      },
      booking: {
        eyebrow: 'Simplifier',
        title: 'Un site où vos clients prennent rendez-vous et règlent leurs prestations.',
        intro: 'Vous proposez des soins, des séances ou d’autres prestations ? Vos clients peuvent choisir leur créneau directement sur votre site, recevoir leur confirmation et, si nécessaire, régler un acompte.',
        problem: 'Vous gérez encore une partie de l’activité par messages, tableaux ou plusieurs services qui ne communiquent pas correctement entre eux.',
        work: ['Prise de rendez-vous sur votre site', 'Paiement ou acompte pour une prestation', 'Emails de confirmation et rappels', 'Interface de gestion simple', 'Commandes, stock ou suivi selon le besoin'],
        budget: 'À partir de 1 250 €. Une réservation simple se situe souvent entre 1 250 € et 1 750 €. Paiement, comptes, back-office ou intégrations peuvent faire évoluer le budget.',
        meta: 'Création de sites où vos clients prennent rendez-vous pour vos prestations, versent un acompte si nécessaire et reçoivent leur confirmation.',
      },
      takeover: {
        eyebrow: 'Reprendre',
        title: 'Changer de développeur ne devrait pas vouloir dire tout recommencer.',
        intro: 'Vous avez déjà investi du temps, de l’argent et des idées dans ce site. Je regarde d’abord ce qui mérite d’être conservé, puis je vous propose la suite sans vous vendre automatiquement une refonte.',
        problem: 'Votre site ne vous ressemble plus, le projet est bloqué, certaines fonctions ne marchent pas ou vous souhaitez simplement travailler avec un autre développeur. La première question, c’est : que peut-on sauver ?',
        work: ['État des lieux du site, du code et des accès', 'Identification des corrections prioritaires', 'Amélioration du design ou des fonctions existantes', 'Mise en ligne et vérifications', 'Accès et passation pour rester libre de changer de prestataire'],
        budget: '350 € à 850 €+ pour une reprise ciblée. Un projet plus profond est chiffré après lecture de l’existant afin de ne pas inventer un prix à l’aveugle.',
        meta: 'Reprise de projet web existant : audit de l’existant, corrections, fonctions manquantes, déploiement et passation.',
      },
    },
  },
  en: {
    back: 'Back to portfolio',
    estimate: 'Estimate this project',
    start: 'Starting point',
    scope: 'What I can take care of',
    budget: 'Budget range',
    budgetNote: 'The final quote depends on the useful scope and what already exists. The goal is a coherent first version, not a pile of features.',
    ctaTitle: 'Still figuring out the scope?',
    ctaText: 'The estimator helps you choose useful features and get a budget range before contacting me.',
    cta: 'Get an estimate',
    contact: 'Discuss the project',
    example: 'Explore a real client project',
    services: {
      creation: {
        eyebrow: 'Build',
        title: 'A website that explains your business clearly and makes people act.',
        intro: 'A useful website does not start with a technology stack. It starts with what your customers need to understand, do or buy.',
        problem: 'You are starting from scratch, your current presence is too limited or your business needs a clearer, more credible website.',
        work: ['Business website or landing page', 'Forms and quote requests', 'Services or catalogue presentation', 'Mobile version and launch', 'E-commerce when useful'],
        budget: '€850 to €1,250 for a simple business website. Projects with sales, accounts or business logic are priced according to the useful scope.',
        meta: 'Custom website creation for independent professionals and small businesses.',
      },
      booking: {
        eyebrow: 'Simplify',
        title: 'A website where customers book your services and pay.',
        intro: 'Offer services or appointments? Customers can choose a time directly on your website, receive confirmation and pay a deposit when needed.',
        problem: 'Part of your business is still managed through messages, spreadsheets or several disconnected services.',
        work: ['Booking and availability', 'Online payment or deposit', 'Confirmation and reminder emails', 'Simple admin interface', 'Orders, stock or tracking when needed'],
        budget: 'From €1,250. A straightforward booking flow is often €1,250 to €1,750. Payments, accounts, admin or integrations can increase the scope.',
        meta: 'Custom online booking, payments and back-office for independent professionals and small businesses.',
      },
      takeover: {
        eyebrow: 'Take over',
        title: 'Your project exists. Now it needs to become usable.',
        intro: 'An unfinished project does not necessarily need a rebuild. I start by understanding what exists, what works and what is actually blocking it.',
        problem: 'The project was started with AI, no-code or another developer, but bugs, missing features or deployment work remain.',
        work: ['Existing project review', 'Blocker prioritisation', 'Targeted fixes and features', 'Deployment support', 'Clear handover'],
        budget: '€350 to €850+ for a targeted takeover. Larger projects are priced after reviewing the existing work.',
        meta: 'Existing web project takeover: review, fixes, missing features, deployment and handover.',
      },
    },
  },
  pt: {
    back: 'Voltar ao portfólio',
    estimate: 'Estimar este projeto',
    start: 'Ponto de partida',
    scope: 'O que posso assumir',
    budget: 'Faixa de orçamento',
    budgetNote: 'O orçamento final depende do escopo útil e do que já existe. O objetivo é lançar uma primeira versão coerente, sem acumular funções desnecessárias.',
    ctaTitle: 'O projeto ainda está pouco definido?',
    ctaText: 'O estimador ajuda a escolher funções úteis e situar o orçamento antes mesmo de entrar em contato.',
    cta: 'Fazer uma estimativa',
    contact: 'Falar do projeto',
    example: 'Conhecer um projeto real',
    services: {
      creation: {
        eyebrow: 'Criar',
        title: 'Um site que explica claramente a sua atividade e leva à ação.',
        intro: 'Um site útil não começa pela tecnologia. Começa pelo que os seus clientes precisam compreender, fazer ou comprar.',
        problem: 'Está a começar do zero, a presença atual é limitada ou a atividade precisa de um site mais claro e profissional.',
        work: ['Site institucional ou landing page', 'Formulários e pedidos de orçamento', 'Apresentação de serviços ou catálogo', 'Versão mobile e publicação', 'E-commerce quando necessário'],
        budget: '850 € a 1.250 € para um site institucional simples. Venda, contas ou lógica de gestão são orçamentadas conforme o escopo.',
        meta: 'Criação de sites sob medida para profissionais independentes e pequenas empresas.',
      },
      booking: {
        eyebrow: 'Simplificar',
        title: 'Um site onde os clientes marcam os seus serviços e pagam.',
        intro: 'Oferece sessões, atendimentos ou serviços? Os clientes podem escolher o horário no seu site, receber confirmação e pagar um sinal quando necessário.',
        problem: 'Parte da atividade ainda é gerida por mensagens, folhas de cálculo ou vários serviços desconectados.',
        work: ['Reservas e disponibilidades', 'Pagamento ou sinal online', 'Emails de confirmação e lembretes', 'Interface de gestão simples', 'Pedidos, stock ou acompanhamento'],
        budget: 'A partir de 1.250 €. Uma reserva simples fica muitas vezes entre 1.250 € e 1.750 €. Pagamentos, contas e integrações podem ampliar o escopo.',
        meta: 'Reservas online, pagamentos e back-office sob medida para pequenas empresas.',
      },
      takeover: {
        eyebrow: 'Retomar',
        title: 'O projeto já existe. Agora precisa de se tornar utilizável.',
        intro: 'Um projeto inacabado não precisa necessariamente de ser refeito. Primeiro é preciso entender o que existe e o que realmente bloqueia.',
        problem: 'O projeto foi iniciado com IA, no-code ou outro programador, mas ainda há bugs, funções em falta ou publicação por concluir.',
        work: ['Análise do existente', 'Priorização dos bloqueios', 'Correções focadas', 'Apoio à publicação', 'Passagem clara'],
        budget: '350 € a 850 €+ para uma retomada focada. Projetos maiores são avaliados depois de analisar o existente.',
        meta: 'Retomada de projeto web existente: análise, correções, funções em falta e publicação.',
      },
    },
  },
};

export default function ServicePage({ service }) {
  const { lang } = useLang();
  const t = COPY[lang] || COPY.fr;
  const data = t.services[service] || t.services.creation;
  const goal = GOALS[service] || 'present';

  useEffect(() => {
    const previousTitle = document.title;
    const description = document.querySelector('meta[name="description"]');
    const previousDescription = description?.getAttribute('content');
    document.title = `${data.title} | Alioune Seck`;
    description?.setAttribute('content', data.meta);
    trackEvent('acquisition_page_view', { page_type: 'service', service });
    return () => {
      document.title = previousTitle;
      if (description && previousDescription) description.setAttribute('content', previousDescription);
    };
  }, [data, service]);

  return (
    <div className="portfolio-v2 acquisition-page-v2">
      <header className="estimator-page-header">
        <Link className="v2-brand" to="/">
          <span className="v2-brand-symbol">AS</span>
          <span><strong>Alioune Seck</strong><small>{data.eyebrow}</small></span>
        </Link>
        <Link className="v2-header-cta" to={`/estimer-mon-projet?goal=${goal}`} onClick={() => trackEvent('cta_click', { placement: 'service_header', target: 'estimator', service })}>{t.estimate} ↗</Link>
      </header>

      <main className="acquisition-main">
        <section className="acquisition-hero">
          <Link className="v2-back" to="/">{t.back}</Link>
          <span className="v2-kicker">{data.eyebrow}</span>
          <h1>{data.title}</h1>
          <p>{data.intro}</p>
          <div className="v2-hero-actions">
            <Link className="v2-primary" to={`/estimer-mon-projet?goal=${goal}`} onClick={() => trackEvent('cta_click', { placement: 'service_hero', target: 'estimator', service })}>{t.estimate} →</Link>
            <Link className="v2-secondary" to="/#contact">{t.contact}</Link>
          </div>
        </section>

        <section className="acquisition-split">
          <article className="acquisition-problem">
            <span className="v2-kicker">{t.start}</span>
            <p>{data.problem}</p>
          </article>
          <article className="acquisition-scope">
            <span className="v2-kicker">{t.scope}</span>
            <ul>{data.work.map((item) => <li key={item}>{item}</li>)}</ul>
            {service !== 'takeover' && (
              <Link className="v2-inline-cta" to="/realisations/atelier-dheloise" onClick={() => trackEvent('project_click', { project: 'atelier', placement: 'service_page' })}>{t.example} ↗</Link>
            )}
          </article>
        </section>

        <section className="acquisition-budget">
          <div>
            <span className="v2-kicker">{t.budget}</span>
            <h2>{data.budget}</h2>
          </div>
          <p>{t.budgetNote}</p>
        </section>

        <section className="acquisition-convert">
          <span className="v2-kicker">Estimation</span>
          <h2>{t.ctaTitle}</h2>
          <p>{t.ctaText}</p>
          <Link className="v2-primary" to={`/estimer-mon-projet?goal=${goal}`} onClick={() => trackEvent('cta_click', { placement: 'service_bottom', target: 'estimator', service })}>{t.cta} →</Link>
        </section>
      </main>
    </div>
  );
}
